import { Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategyOrigin,
  Overlay
} from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import { JepMapView, JepViewClickEvent } from '@jeppesen-foreflight/jmkw-types';
import { Observable, Subject } from 'rxjs';
import { AVIATION_CONFIG_TOKEN } from '../../../../aviation.config';
import { FeatureHighlightService } from './feature-highlight.service';
import { DisambiguationDialogComponent } from '../disambiguation-popup/disambiguation-dialog.component';
import { FeatureDetailService } from '../../../../utilities/feature-detail.service';
import { FeatureDetail } from '../../typings/features/feature-detail';
import type { GraphicHit } from "@arcgis/core/views/types";

// This code was generated by Generative AI (DO NOT DELETE)
@Injectable({ providedIn: 'root' })
export class DisambiguationDialogService {
  private readonly _dialog = inject(Dialog);
  private readonly _overlay: Overlay = inject(Overlay);
  private readonly _api = inject(ToolkitApiService);
  private readonly _config = inject(AVIATION_CONFIG_TOKEN);
  private readonly _featureDetailSvc = inject(FeatureDetailService);
  private readonly _featureHighlightSvc = inject(FeatureHighlightService);

  dialogRef!: DialogRef<FeatureDetail, DisambiguationDialogComponent> | null;

  /**
   * Handles the disambiguation of multiple features based on user interaction.
   *
   * @param event - The click event on the map view.
   * @param viewHits - An array of graphic hits from the map view.
   * @returns An observable that emits the selected feature detail.
   */
  handleMultipleFeatureDisambiguation(
    event: JepViewClickEvent,
    viewHits: GraphicHit[]
  ): Observable<FeatureDetail> {
    const deduped: FeatureDetail[] = this._dedupeViewHits(viewHits).map(
      (hit: GraphicHit) => {
        return this._featureDetailSvc.createFromGraphic(hit.graphic);
      }
    );
    return this._initiateUserInteraction(event, deduped);
  }

  /**
   * Deduplicates the graphic hits based on their navigation type and entity ID.
   *
   * @param viewHits - An array of graphic hits to be deduplicated.
   * @returns An array of unique graphic hits.
   */
  private _dedupeViewHits(viewHits: GraphicHit[]): GraphicHit[] {
    const uniqueMap = new Map<string, GraphicHit>();

    viewHits.forEach((hit) => {
      const { nav_type, eid } = hit.graphic.attributes;
      const uniqueKey = `${nav_type}-${eid}`;

      if (!uniqueMap.has(uniqueKey)) {
        uniqueMap.set(uniqueKey, hit);
      }
    });

    return Array.from(uniqueMap.values());
  }

  /**
   * Initiates user interaction by opening a disambiguation dialog and handling the selection.
   *
   * @param event - The click event on the map view.
   * @param candidates - An array of feature details to present in the dialog.
   * @returns An observable that emits the selected feature detail.
   */
  private _initiateUserInteraction(
    event: JepViewClickEvent,
    candidates: FeatureDetail[]
  ): Observable<FeatureDetail> {
    const selectionSubject: Subject<FeatureDetail> =
      new Subject<FeatureDetail>();

    // Only add click origin highlight if default click handler is enabled
    if (this._config.useDefaultClickHandler !== false) {
      this._featureHighlightSvc.addClickOriginHighlight(event.mapPoint);
    }

    this._openDisambiguationDialog(candidates);
    this.dialogRef?.closed.subscribe((selection: FeatureDetail | undefined) => {
      // removeClickOriginHighlight safely handles the case where no highlight was added
      this._featureHighlightSvc.removeClickOriginHighlight();
      if (selection) {
        selectionSubject.next(selection);
      }
      selectionSubject.complete();
    });

    return selectionSubject.asObservable();
  }

  /**
   * Opens the disambiguation dialog with the provided candidates.
   *
   * @param candidates - An array of feature details to display in the dialog.
   * @returns A reference to the opened dialog.
   */
  private _openDisambiguationDialog(
    candidates: FeatureDetail[]
  ): DialogRef<FeatureDetail, DisambiguationDialogComponent> | void {
    this.dialogRef?.close();
    const view: JepMapView = this._api.getMapView();
    const center = view.toScreen(view.center);
    if (!center) {
      throw new Error(
        'Unable to determine screen center for dialog positioning.'
      );
    }
    const top = center.y + 10;
    const maxHeight = window.innerHeight - top;

    this.dialogRef = this._dialog.open(DisambiguationDialogComponent, {
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo({
          x: center.x + 30, // Add a 30px offset
          y: center.y + 30
        } as FlexibleConnectedPositionStrategyOrigin)
        .withPositions([
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top'
          } as ConnectedPosition
        ])
        .withPush(false),
      panelClass: 'dialog-arrow-right',
      maxHeight: maxHeight + 'px',
      data: candidates
    });

    return this.dialogRef;
  }
}
